import { useMemo } from "react";
import { useNavigate } from "react-router";

import type { SystemGroupDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { PillList } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface PlatformGroupsListProps {
  platformGroups: SystemGroupDto[];
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  onDelete: (groupId: string) => Promise<unknown>;
}

export function PlatformGroupsList({
  platformGroups,
  isLoading,
  page,
  onPageChange,
  totalPages,
  onDelete,
}: PlatformGroupsListProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const { componentProps: deleteModalProps, openDeleteModal } =
    useDeleteModal<SystemGroupDto>("platform-group-delete-modal");

  const { data: projects = [] } = useLinkedProjects();

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<SystemGroupDto>();

    const getName = (group: SystemGroupDto) => {
      return (
        group.translations?.find((l) => l.languageId === sessionUser.language.id)?.name ||
        group.translations?.[0]?.name ||
        "-"
      );
    };

    return [
      helper.accessor("translations", {
        header: t("page.platform-groups.list.table.name"),
        cell: (cell) => getName(cell.row.original),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => getName(cell.row.original),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("type", {
        header: t("page.platform-groups.list.table.type"),
        cell: (cell) => {
          switch (cell.getValue()) {
            case "interest":
              return <span className="whitespace-nowrap">{t("page.platform-groups.list.type.interest-group")}</span>;
            case "helpCategory":
              return <span className="whitespace-nowrap">{t("page.platform-groups.list.type.help-category")}</span>;
          }
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("excludedProjects", {
        header: t("page.platform-groups.list.table.enabled-projects"),
        cell: (cell) => (
          <PillList
            items={projects
              .filter(
                (p) =>
                  !cell
                    .getValue()
                    ?.map((p) => p.id)
                    .includes(p.id),
              )
              .map((project) => ({
                label: project.name,
              }))}
            maxShown={3}
          />
        ),
        minSize: TABLE_COLUMN_SIZES.TAGS.MIN,
        size: TABLE_COLUMN_SIZES.TAGS.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();

          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => void navigate(routes.platformGroups.edit({ id })),
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => openDeleteModal(cell.row.original),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, sessionUser.language.id, projects, navigate, openDeleteModal]);

  const tableInstance = useResizableTable(TABLE_KEYS.PLATFORM_GROUPS, {
    columns,
    data: platformGroups,
    enableSorting: false,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.platform-groups.list.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("page.platform-groups.list.title"),
            },
          ]}
        />
      }
      actions={
        <Button type="link" href={routes.platformGroups.create()}>
          {t("page.platform-groups.list.new-group")}
        </Button>
      }
    >
      {(platformGroups.length > 0 || isLoading) && (
        <Table
          table={tableInstance}
          isLoading={isLoading}
          getRowHref={(row) => routes.platformGroups.edit({ id: row.original.id })}
        >
          <Pagination count={totalPages} onChange={onPageChange} currentIndex={page} />
        </Table>
      )}
      {!isLoading && !platformGroups.length && (
        <SectionCard>
          <p>{t("page.platform-groups.list.table.no-data")}</p>
        </SectionCard>
      )}
      <DeleteModal
        title={t("page.platform-groups.modal.delete.title", {
          group: deleteModalProps.modalContext
            ? getPlatformGroupName(deleteModalProps.modalContext, sessionUser.language.id)
            : "-",
        })}
        description={t("page.platform-groups.modal.delete.description")}
        onDelete={(group) => onDelete(group.id)}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}

function getPlatformGroupName(group: SystemGroupDto, languageId: string) {
  return group.translations?.find((x) => x.languageId === languageId)?.name || group.translations?.[0]?.name || "-";
}
